import { Link } from "react-router-dom";
import { Check, Circle, XCircle, PauseCircle, UploadCloud, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const DOCUMENTS_LINK_TEXT = "eazy-travels.com/documents";

// "Documents Approved" and "Documents Rejected" are two mutually exclusive OUTCOMES of the
// same review step, not two stages a case moves through in sequence — so rendering them as
// consecutive timeline entries made the one that *didn't* happen show up as a confusing
// "next step". This collapses that specific pair into a single line: an undecided
// placeholder while review is still in progress, then only whichever outcome was reached.
function buildDisplayStages(stages, currentStageIndex) {
  const approvedIdx = stages.findIndex((s) => s.label.trim().toLowerCase() === "documents approved");
  const rejectedIdx = stages.findIndex((s) => s.label.trim().toLowerCase() === "documents rejected");

  if (approvedIdx === -1 || rejectedIdx === -1) {
    return stages.map((stage, idx) => ({ label: stage.label, completed_at: stage.completed_at, stageIndex: idx }));
  }

  const mergedIdx = Math.min(approvedIdx, rejectedIdx);
  const otherIdx = Math.max(approvedIdx, rejectedIdx);

  let decidedIdx = null;
  if (currentStageIndex === approvedIdx || currentStageIndex === rejectedIdx) {
    decidedIdx = currentStageIndex;
  } else if (currentStageIndex > otherIdx) {
    // Case moved on past both branch stages — infer which was actually reached. Approved
    // is the default read since normal progress never continues forward past a rejection
    // without first looping back through document resubmission.
    decidedIdx = stages[approvedIdx].completed_at ? approvedIdx : stages[rejectedIdx].completed_at ? rejectedIdx : null;
  }

  const display = [];
  stages.forEach((stage, idx) => {
    if (idx === otherIdx) return; // the branch not taken never gets its own line
    if (idx === mergedIdx) {
      display.push({
        label: decidedIdx != null ? stages[decidedIdx].label : "Documents Approved / Rejected",
        completed_at: decidedIdx != null ? stages[decidedIdx].completed_at : null,
        stageIndex: decidedIdx != null ? decidedIdx : mergedIdx,
      });
      return;
    }
    display.push({ label: stage.label, completed_at: stage.completed_at, stageIndex: idx });
  });
  return display;
}

// Fixed system copy keyed off the exact stage label the admin set — matches the vocabulary
// enforced by the New Application stage dropdown (CasesPanel.js's STAGE_OPTIONS).
const ACTION_BANNERS = {
  "awaiting documents": { icon: UploadCloud, className: "border-amber-400/30 bg-amber-400/5 text-amber-300", prefixKey: "caseStatus.awaitingDocumentsPrefix", withLink: true },
  "documents rejected": { icon: AlertTriangle, className: "border-red-400/30 bg-red-400/5 text-red-300", prefixKey: "caseStatus.documentsRejectedPrefix", withLink: true },
  "documents approved": { icon: CheckCircle2, className: "border-emerald-400/30 bg-emerald-400/5 text-emerald-300", messageKey: "caseStatus.documentsApprovedBanner", withLink: false },
  "completed": { icon: CheckCircle2, className: "border-emerald-400/30 bg-emerald-400/5 text-emerald-300", messageKey: "caseStatus.documentsApprovedBanner", withLink: false },
};

export default function CaseProgress({ caseItem }) {
  const { t } = useLang();
  const { title, service_type, stages, current_stage_index, status, notes, updated_at, rejection_reasons } = caseItem;

  // Status label/color is fixed UI chrome (translatable); stage labels and notes below
  // are free text staff typed into the Cases admin panel — never translated, shown as-is.
  const STATUS_BADGE = {
    active: { label: t("caseStatus.active"), className: "border-[var(--gold)]/25 text-[var(--gold)]" },
    completed: { label: t("caseStatus.completed"), className: "border-emerald-400/30 text-emerald-400" },
    on_hold: { label: t("caseStatus.onHold"), className: "border-yellow-400/30 text-yellow-400" },
    cancelled: { label: t("caseStatus.cancelled"), className: "border-red-400/30 text-red-400" },
  };
  const badge = STATUS_BADGE[status] || STATUS_BADGE.active;
  const currentStageLabel = stages[current_stage_index]?.label;
  const banner = currentStageLabel ? ACTION_BANNERS[currentStageLabel.trim().toLowerCase()] : null;
  const displayStages = buildDisplayStages(stages, current_stage_index);
  const BannerIcon = banner?.icon;

  return (
    <div data-testid={`case-progress-${caseItem.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="font-display text-lg font-bold text-white">{title}</h3>
          {service_type && <p className="text-xs text-white/45 mt-0.5">{service_type}</p>}
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full border shrink-0 ${badge.className}`}>{badge.label}</span>
      </div>
      {updated_at && (
        <p className="text-xs text-white/40 mb-3">{t("caseStatus.lastUpdated")} {new Date(updated_at).toLocaleDateString()}</p>
      )}

      {currentStageLabel && (
        <div
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[var(--gold)]/30 bg-[var(--gold)]/10 mb-3"
          data-testid={`case-current-stage-${caseItem.id}`}
        >
          <span className="text-[10px] uppercase tracking-widest text-white/50">{t("caseStatus.currentStage")}</span>
          <span className="text-sm font-semibold text-[var(--gold)]">{currentStageLabel}</span>
        </div>
      )}

      {currentStageLabel?.trim().toLowerCase() === "documents rejected" && rejection_reasons?.length > 0 && (
        <div className="mb-3" data-testid={`case-rejection-reasons-${caseItem.id}`}>
          <p className="text-xs uppercase tracking-widest text-white/40 mb-1.5">{t("caseStatus.rejectionReasonLabel")}</p>
          <ul className="space-y-1">
            {rejection_reasons.map((reason, i) => (
              <li key={i} className="text-sm text-red-300/90 flex items-start gap-2">
                <span className="text-red-400/60 mt-0.5">•</span> {reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {banner && (
        <div className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 text-sm mb-3 ${banner.className}`} data-testid={`case-action-banner-${caseItem.id}`}>
          <BannerIcon size={16} className="shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            {banner.withLink ? (
              <>
                {t(banner.prefixKey)}
                <Link to="/documents" className="underline hover:no-underline">{DOCUMENTS_LINK_TEXT}</Link>
              </>
            ) : (
              t(banner.messageKey)
            )}
          </p>
        </div>
      )}

      <ol className="space-y-0 mt-4">
        {displayStages.map((stage, i) => {
          const isDone = !!stage.completed_at;
          const isCurrent = stage.stageIndex === current_stage_index && status !== "cancelled";
          const isLast = i === displayStages.length - 1;
          return (
            <li key={i} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div
                  className={`w-7 h-7 shrink-0 grid place-items-center rounded-full border-2 ${
                    isDone
                      ? "bg-[var(--gold)] border-[var(--gold)] text-[#0a0a0a]"
                      : isCurrent
                      ? "border-[var(--gold)] text-[var(--gold)]"
                      : "border-white/15 text-white/30"
                  }`}
                >
                  {isDone ? <Check size={14} /> : status === "cancelled" ? <XCircle size={14} /> : <Circle size={10} fill="currentColor" />}
                </div>
                {!isLast && <div className={`w-0.5 flex-1 min-h-[24px] ${isDone ? "bg-[var(--gold)]" : "bg-white/10"}`} />}
              </div>
              <div className="pb-6">
                <p className={`text-sm font-medium ${isDone || isCurrent ? "text-white" : "text-white/45"}`}>
                  {stage.label}
                </p>
                {isCurrent && status !== "cancelled" && (
                  <p className="text-xs text-[var(--gold)] mt-0.5">{t("caseStatus.inProgress")}</p>
                )}
                {isDone && (
                  <p className="text-xs text-white/40 mt-0.5">{new Date(stage.completed_at).toLocaleDateString()}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {status === "on_hold" && (
        <p className="text-xs text-yellow-400/80 flex items-center gap-1.5 mt-1">
          <PauseCircle size={13} /> {t("caseStatus.onHoldNotice")}
        </p>
      )}
      {notes && (
        <div className="mt-2 pt-4 border-t border-white/5">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-1.5">{t("caseStatus.notesFromTeam")}</p>
          <p className="text-sm text-white/70 whitespace-pre-wrap">{notes}</p>
        </div>
      )}
    </div>
  );
}
